/**
 * Performance mode - OPButtons' remote for the ONE suite-wide switch.
 *
 * REDONE 2026-09-25 (OPBridge S285 ask, operator direction). The old shape
 * predated OPBridge's switch: OPButtons kept its own `uiMode: 'performance'`
 * beside OPBridge's flag, ORed the two together, and bundled a locked stage
 * UI into the switch. Two operator rules replaced it:
 *
 *   - "a master toggle switch in all apps that use it" - there is exactly one
 *     performance mode and OPBridge holds it. This module is a remote for it,
 *     never a private copy (OPBridge PROTOCOL.md "Performance mode - ONE
 *     suite-wide switch", required behaviour 1-4).
 *   - "performance mode shouldn't change view, just background stuff" -
 *     nothing on screen moves when it flips (required behaviour 5). What
 *     reads `performanceMode()` is background only: the project sync poll,
 *     the wake lock, and the pre-flight run. See DEC-216.
 *
 * THE RULES THIS FOLLOWS, in the order they bite:
 *   1. A press updates our own state straight away, then POSTs
 *      `/performance-mode` to the PRIMARY. One bridge is enough - the mesh
 *      carries it to the other machine.
 *   2. Switches made anywhere else arrive as the `performance_mode_changed`
 *      push on `/ws/consumer`, and on `/health` (`capabilities.
 *      performance_mode`). The health poll already runs every ~2s for
 *      connection status, so it is read on every tick rather than only on
 *      reconnect - a dead socket then costs two seconds, not a missed switch.
 *   3. A press that cannot be sent (OPBridge unreachable) is KEPT and sent
 *      when the bridge comes back. `/health` is NOT adopted until it lands:
 *      its value is older than the user's press. Persisted, so a reload in
 *      that window does not quietly drop the performer's choice.
 *   4. For a short window after our own POST lands, `/health` is ignored: a
 *      poll that left before the POST can answer after it and would flick the
 *      toggle back for one tick.
 *
 * A 4xx on the POST is terminal for this bearer (a 403 is an old hand-scoped
 * token without the scope; a 404 is a daemon too old to have the route), so
 * the press is abandoned, the toggle returns to OPBridge's value and the
 * reason is shown. Retrying would never succeed.
 */

import { batch, createSignal, createEffect, createRoot, untrack, type Accessor } from 'solid-js';
import { primaryBridge, getConnection, connectionsVersion } from './store.ts';
import { OpbridgeError, type OpbridgeHealth } from './httpClient.ts';
import {
  onPerformanceModeChanged, parsePerformanceModeSource, type PerformanceModeSource,
} from './consumerWs.ts';

export type { PerformanceModeSource };

/** Longer than one health poll (2s) plus its timeout (1.2s). */
const SETTLE_MS = 3_500;

/** A press OPBridge has not accepted yet: '1' / '0' is the wanted state. */
const PENDING_KEY = 'opbuttons.performanceMode.pending.v1';

/** Harness kill switch. When '1', a press is never sent to OPBridge - it
 *  stays local and pending. Following switches made elsewhere is unaffected
 *  (reading is harmless).
 *
 *  WHY: smokes run on a real dev-PC bearer (`realDevPcRow`). Sending this
 *  switch from one would put the operator's WHOLE suite - OPBridge, OPVisual,
 *  both machines - into performance mode. Same shape as ruleSync's switch;
 *  `BotClient` sets it unless a smoke opts in, and an opted-in smoke has the
 *  write intercepted on the browser context. */
const DISABLE_KEY = 'opbuttons.performanceSwitch.disabled';

function readStorage(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch { /* private mode / quota - the press still works this session */ }
}
function sendDisabled(): boolean { return readStorage(DISABLE_KEY) === '1'; }

const persisted = readStorage(PENDING_KEY);
const [enabledSig, setEnabled] = createSignal(persisted === '1');
const [sourceSig, setSource] = createSignal<PerformanceModeSource | null>(null);
const [pendingSig, setPending] = createSignal(persisted === '1' || persisted === '0');
const [problemSig, setProblem] = createSignal<string | null>(null);
/** Has this device heard the state yet - from OPBridge (health or push) or
 *  from its own press (a persisted unsent press counts: it IS the wanted
 *  state). False from page load until then, which after a reload mid-gig is
 *  the first second or two. */
const [knownSig, setKnown] = createSignal(persisted === '1' || persisted === '0');

/** Is the suite in performance mode, as far as this device knows. */
export const performanceMode: Accessor<boolean> = enabledSig;
/** True once `performanceMode()` reflects something real rather than the
 *  page-load default of `false` (2026-09-30). Anything that must not act
 *  WHILE performing - an app update reloading the page - must wait for this:
 *  "not known yet" is not "off". */
export const performanceModeKnown: Accessor<boolean> = knownSig;
/** Who switched it, when OPBridge said. Null when unknown. */
export const performanceModeSource: Accessor<PerformanceModeSource | null> = sourceSig;
/** True while a press made here has not reached OPBridge yet. */
export const performanceModeUnsent: Accessor<boolean> = pendingSig;
/** Why the last press could not be applied, for the toggle's status line. */
export const performanceModeProblem: Accessor<string | null> = problemSig;

/** Which consumer id this app reports as - matches `source.app`. */
export const OWN_APP_ID = 'opbuttons';

let settleUntil = 0;
let inFlight = false;
/** OPBridge's last reported value, so an abandoned press can go back to it. */
let lastBridgeValue: boolean | null = null;
let lastBridgeSource: PerformanceModeSource | null = null;

/** Switch performance mode for the whole suite. Updates this device at once;
 *  the POST follows, now or on reconnect. */
export function setPerformanceMode(on: boolean): void {
  // ONE update (2026-10-06). As separate writes, `known` turned true while
  // `enabled` was still false, so anything reading "known and not performing"
  // saw a show end at the instant one STARTED: the held error offer popped
  // up as the switch went ON (crash_offer_waits_for_performance_mode found
  // it), and a ready app update could have reloaded the page in that instant.
  batch(() => {
    setEnabled(on);
    setKnown(true);
    setSource({ app: OWN_APP_ID, appName: 'OPButtons', machine: null });
    setProblem(null);
    setPending(true);
  });
  writeStorage(PENDING_KEY, on ? '1' : '0');
  void flush();
}

function primaryConnection() {
  void connectionsVersion(); // getConnection() is a bare Map.get - track swaps
  const p = primaryBridge();
  return p ? getConnection(p.id) ?? null : null;
}

/** True while a press made here is waiting because OPBridge is unreachable
 *  (as opposed to a POST that is simply in flight). Reactive. */
export function performanceModeWaitingForBridge(): boolean {
  if (!pendingSig()) return false;
  return primaryConnection()?.status() !== 'connected';
}

async function flush(): Promise<void> {
  if (inFlight || !untrack(pendingSig) || sendDisabled()) return;
  const conn = untrack(primaryConnection);
  if (!conn || conn.status() !== 'connected') return;
  const want = untrack(enabledSig);
  inFlight = true;
  try {
    await conn.httpClient().setPerformanceMode(want);
    if (untrack(enabledSig) === want) {
      setPending(false);
      writeStorage(PENDING_KEY, null);
      settleUntil = Date.now() + SETTLE_MS;
    }
  } catch (err) {
    const status = err instanceof OpbridgeError ? err.httpStatus : undefined;
    const terminal = err instanceof OpbridgeError
      && (err.kind === 'unauthorized' || (status !== undefined && status >= 400 && status < 500));
    if (terminal) abandon(status);
    // network / timeout / 5xx: stay pending; the next health tick retries.
  } finally {
    inFlight = false;
  }
  // Pressed again while that POST was out - send the newer state.
  if (untrack(pendingSig) && untrack(enabledSig) !== want) void flush();
}

function abandon(status: number | undefined): void {
  setPending(false);
  writeStorage(PENDING_KEY, null);
  setProblem(
    status === 403 ? "This device's pairing can't switch performance mode. Re-pair it to fix that."
      : status === 404 ? 'OPBridge on this machine is too old for the suite performance switch. Update it.'
      : status === 401 || status === undefined ? 'OPBridge no longer accepts this device. Re-pair it.'
      : `OPBridge refused the switch (${status}).`,
  );
  setEnabled(lastBridgeValue ?? false);
  setSource(lastBridgeSource);
}

function onHealth(h: OpbridgeHealth): void {
  const v = h.capabilities?.['performance_mode'];
  if (typeof v === 'boolean') {
    lastBridgeValue = v;
    lastBridgeSource = parsePerformanceModeSource(h.capabilities['performance_mode_source']);
  }
  // Known only once a value is actually adopted below (or a press of ours
  // stands): during the settle window the value in hand is older than our own
  // press, which already set `known`.
  if (pendingSig()) { void flush(); return; }
  if (Date.now() < settleUntil || typeof v !== 'boolean') return;
  batch(() => {
    setEnabled(v);
    setSource(lastBridgeSource);
    setKnown(true);
  });
}

let installed = false;

/** Wire the health follower and the push listener. Idempotent; called once
 *  from App. */
export function installPerformanceModeSync(): void {
  if (installed) return;
  installed = true;
  createRoot(() => {
    // Re-runs on every health tick: each tick writes a fresh object.
    // `connectionsVersion()` is read so a primary whose connection object is
    // swapped on the same id (DEC-526 suite-hostname upgrade) is followed -
    // without it this kept reading the OLD connection forever (2026-08-23).
    createEffect(() => {
      const conn = primaryConnection();
      const h = conn?.healthSignal()();
      if (h) untrack(() => onHealth(h));
    });
  });
  onPerformanceModeChanged((e) => {
    if (untrack(pendingSig)) return; // our unsent press is newer
    lastBridgeValue = e.enabled;
    lastBridgeSource = e.source;
    batch(() => {
      setEnabled(e.enabled);
      setSource(e.source);
      setKnown(true);
    });
    settleUntil = Date.now() + SETTLE_MS;
  });
}

/** "ON - from OPVisual on Mac Studio". Null when OPBridge did not say. */
export function describeSource(s: PerformanceModeSource | null): string | null {
  if (!s) return null;
  const who = s.appName ?? s.app;
  return s.machine ? `${who} on ${s.machine}` : who;
}
